/**
 * Submenu toggle voor de AgoraWalker.
 *
 * Werkt zowel op de desktop primary navigation als op de mobile drawer
 * — de walker rendert in beide gevallen `[data-agora-nav-toggle]`
 * buttons met `aria-expanded` en `aria-controls`. JS schrijft alleen
 * de aria-expanded state; visibility van submenus wordt door SCSS
 * bepaald (zie `_site-navigation.scss` / `_mobile-nav.scss` voor de
 * `[aria-expanded="true"]` ~ submenu selector). Daardoor conflicteren
 * desktop hover (CSS-only) en click-toggle niet.
 *
 * Idempotent via `data-agora-nav-toggle-bound="1"` flag — herhaalde
 * `initNavSubmenu()` calls binden dezelfde button niet twee keer.
 *
 * @module modules/ui/nav-submenu
 */

const SELECTORS = {
    toggle: '[data-agora-nav-toggle]',
};

/**
 * True wanneer de viewport-mediaquery `(hover: hover)` matcht — dat
 * is het signaal voor "echte" pointer (muis, trackpad). Op die
 * devices laten we click-outside een open submenu sluiten; in mobile
 * drawer (touch / no-hover) is de submenu een persistent accordion.
 *
 * @returns {boolean}
 */
function isHoverCapable() {
    return typeof window.matchMedia === 'function'
        && window.matchMedia('(hover: hover)').matches;
}

/**
 * Sluit een submenu (zet aria-expanded="false") en geef focus terug
 * aan de toggle wanneer dat gewenst is.
 *
 * @param {HTMLElement} toggle
 * @param {{ focus?: boolean }} [options]
 */
function closeSubmenu(toggle, options = {}) {
    toggle.setAttribute('aria-expanded', 'false');
    const submenu = getSubmenuForToggle(toggle);
    if (submenu) resetSubmenuPosition(submenu);
    if (options.focus) {
        toggle.focus();
    }
}

/**
 * Resolveert het submenu dat bij een toggle-button hoort. Eerste keuze
 * is `aria-controls` → element-by-id (de walker zet beide consistent).
 * Fallback is een sibling-search binnen de bevattende `<li>` voor
 * markup-varianten die geen id hebben.
 *
 * @param {HTMLElement} toggle
 * @returns {HTMLElement|null}
 */
function getSubmenuForToggle(toggle) {
    const id = toggle.getAttribute('aria-controls');
    if (id) {
        const el = document.getElementById(id);
        if (el instanceof HTMLElement) return el;
    }
    const item = toggle.closest('li');
    if (!item) return null;
    const fallback = item.querySelector(':scope > [class*="__submenu"]');
    return fallback instanceof HTMLElement ? fallback : null;
}

/**
 * Verwijdert eerder gezette inline positioning zodat het submenu bij
 * de volgende open weer met een schone slate begint (en CSS-defaults
 * weer leiden).
 *
 * @param {HTMLElement} submenu
 */
function resetSubmenuPosition(submenu) {
    submenu.style.removeProperty('left');
    submenu.style.removeProperty('right');
    submenu.style.removeProperty('top');
    submenu.style.removeProperty('bottom');
}

/**
 * Positioneert een submenu binnen het viewport. Flipt naar links
 * wanneer het rechts uit het viewport valt en naar boven wanneer het
 * onder uit valt. Dit is een desktop-only feature: in de mobile drawer
 * stapelen submenus in-flow (accordion) en is positionering n.v.t.
 *
 * Implementatie: we lezen eerst de gerenderde `getBoundingClientRect()`
 * en zetten daarna inline overrides. Een tweede meting-en-correctie is
 * niet nodig omdat we alleen flippen, niet schalen — de breedte/hoogte
 * verandert niet.
 *
 * @param {HTMLElement} submenu
 */
function positionSubmenu(submenu) {
    if (!isHoverCapable()) return;

    // Mobile drawer guard: submenu's binnen `.mobile-nav` zijn in-flow
    // accordions, daar mag geen viewport-flip op.
    if (submenu.closest('.mobile-nav')) return;

    resetSubmenuPosition(submenu);

    const rect = submenu.getBoundingClientRect();

    if (rect.right > window.innerWidth) {
        submenu.style.left = 'auto';
        submenu.style.right = '0';
    }

    if (rect.bottom > window.innerHeight) {
        submenu.style.top = 'auto';
        submenu.style.bottom = '100%';
    }
}

/**
 * Sluit alle open submenus binnen de gegeven root, behalve een
 * eventueel uitgezonderd item (bijv. de toggle die net is geklikt).
 *
 * @param {Document|Element} root
 * @param {HTMLElement|null} [except]
 */
function closeAllExcept(root, except = null) {
    root.querySelectorAll(`${SELECTORS.toggle}[aria-expanded="true"]`).forEach((toggle) => {
        if (toggle === except) return;
        toggle.setAttribute('aria-expanded', 'false');
    });
}

/**
 * Public init. Bindt click-handler op alle nav-toggle buttons in de
 * DOM, plus document-level Escape en click-outside listeners.
 */
export function initNavSubmenu() {
    const toggles = document.querySelectorAll(SELECTORS.toggle);
    if (toggles.length === 0) return;

    let documentListenersBound = false;

    toggles.forEach((toggle) => {
        if (!(toggle instanceof HTMLElement)) return;
        if (toggle.dataset.agoraNavToggleBound === '1') return;
        toggle.dataset.agoraNavToggleBound = '1';

        toggle.addEventListener('click', (e) => {
            // Voor links-met-children rendert de walker een aparte
            // <a> + <button>; click op de button moet de link niet
            // triggeren (bubbling naar parent <li>) en geen page
            // reload doen.
            e.preventDefault();
            e.stopPropagation();

            const isOpen = toggle.getAttribute('aria-expanded') === 'true';
            // Alleen op desktop: openen sluit andere submenus op
            // hetzelfde top-level. In mobile drawer is accordion-
            // gedrag de norm — meerdere takken mogen open zijn.
            if (!isOpen && isHoverCapable()) {
                const parent = toggle.closest('ul');
                if (parent) closeAllExcept(parent, toggle);
            }
            const willOpen = !isOpen;
            toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');

            // Viewport-aware positioning bij open op desktop; reset
            // bij close zodat de volgende open weer met CSS-defaults
            // begint (geen "kleeft" naar rechts/boven).
            const submenu = getSubmenuForToggle(toggle);
            if (submenu) {
                if (willOpen) {
                    positionSubmenu(submenu);
                } else {
                    resetSubmenuPosition(submenu);
                }
            }
        });

        documentListenersBound = true;
    });

    // Hover-open positionering (desktop only): SCSS toont de submenu
    // via `:hover`, dus JS heeft hier geen aria-state om aan te haken.
    // We binden mouseenter op items met children en flippen het submenu
    // wanneer nodig. Idempotent via dataset-flag.
    document.querySelectorAll('[data-agora-nav-parent]').forEach((item) => {
        if (!(item instanceof HTMLElement)) return;
        if (item.dataset.agoraNavParentBound === '1') return;
        item.dataset.agoraNavParentBound = '1';

        item.addEventListener('mouseenter', () => {
            if (!isHoverCapable()) return;
            const submenu = item.querySelector(':scope > [class*="__submenu"]');
            if (submenu instanceof HTMLElement) {
                positionSubmenu(submenu);
            }
        });

        item.addEventListener('mouseleave', () => {
            if (!isHoverCapable()) return;
            const submenu = item.querySelector(':scope > [class*="__submenu"]');
            if (submenu instanceof HTMLElement) {
                // Alleen resetten wanneer er geen click-toggle open is
                // — anders verliest de geklikte open submenu zijn flip
                // bij muis-uitloop.
                const innerToggle = item.querySelector(`${SELECTORS.toggle}[aria-expanded="true"]`);
                if (!innerToggle) {
                    resetSubmenuPosition(submenu);
                }
            }
        });
    });

    if (documentListenersBound && !document.documentElement.dataset.agoraNavSubmenuBound) {
        document.documentElement.dataset.agoraNavSubmenuBound = '1';

        // Escape: sluit een open submenu en focus terug naar toggle.
        document.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;
            const open = document.querySelector(`${SELECTORS.toggle}[aria-expanded="true"]`);
            if (open instanceof HTMLElement) {
                closeSubmenu(open, { focus: true });
            }
        });

        // Click-outside: alleen op desktop (hover-capable). Mobile
        // drawer accordion blijft open totdat de gebruiker hem
        // expliciet dichtklikt.
        document.addEventListener('click', (e) => {
            if (!isHoverCapable()) return;
            if (!(e.target instanceof Element)) return;

            // Klik in een toggle-button of in een submenu zelf? Niets doen.
            if (e.target.closest(SELECTORS.toggle)) return;
            if (e.target.closest('[class*="__submenu"]')) return;

            // Sluit alle open submenus en reset hun inline positionering.
            document
                .querySelectorAll(`${SELECTORS.toggle}[aria-expanded="true"]`)
                .forEach((toggle) => {
                    toggle.setAttribute('aria-expanded', 'false');
                    const submenu = getSubmenuForToggle(toggle);
                    if (submenu) resetSubmenuPosition(submenu);
                });
        });
    }
}
